// Rolling digits for a repainted price (see personal-price.js): every digit that
// changed slides up or down to its new value, like an old arrival board. Only the numbers
// move; currency symbol, separators and the del/ins markup are swapped as they are. While
// the recalculation pulse (son-price-loading) runs, the digits wait at their old values and
// roll once discount-toast.js ends it. Styling lives in scss/_price-roll.scss.

const DURATION = 600;
const STAGGER = 40;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// a struck-through amount and the current one are matched by role, not position: a price
// can gain or lose its del between two answers
const role = (bdi) => (bdi.closest('del') ? 'del' : 'now');

function numberNode(bdi) {
    return [...bdi.childNodes].find((child) => child.nodeType === Node.TEXT_NODE && /\d/.test(child.textContent));
}

// what the amount shows right now, also halfway through a roll that is still waiting
function shownNumber(bdi) {
    const roll = bdi.querySelector('.son-price-roll');
    if (roll) {
        return roll.dataset.shown;
    }

    return numberNode(bdi)?.textContent ?? null;
}

// Width of each digit 0-9 in the font of el, measured once per font. A wheel's window
// follows the width of the digit it shows, so the number keeps its spacing.
const digitWidthCache = new Map();

function digitWidths(el) {
    const style = getComputedStyle(el);
    const key = [style.font, style.letterSpacing, style.fontVariantNumeric, style.fontFeatureSettings].join('|');
    if (!digitWidthCache.has(key)) {
        const probe = document.createElement('span');
        probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap';
        probe.innerHTML = [...'0123456789'].map((d) => `<span>${d}</span>`).join('');
        el.append(probe);
        digitWidthCache.set(key, [...probe.children].map((d) => d.getBoundingClientRect().width));
        probe.remove();
    }

    return digitWidthCache.get(key);
}

function amounts(node) {
    return [...node.querySelectorAll('.woocommerce-Price-amount bdi')];
}

// One wheel: the digits it passes, top to bottom, and the cells it starts and ends on. Rising
// prices roll every wheel up, falling ones down, wrapping through 0 instead of turning back.
function wheel(old, next, up, extraTurn, delay, widths) {
    const steps = ((up ? next - old : old - next) + 10) % 10 + (extraTurn ? 10 : 0);
    const cells = [];
    for (let i = 0; i <= steps; i += 1) {
        cells.push((old + (up ? i : -i) + 20) % 10);
    }
    // a falling wheel moves down: its strip runs from the new digit to the old one
    if (!up) {
        cells.reverse();
    }

    const digit = document.createElement('span');
    digit.className = 'son-price-roll__digit';
    digit.innerHTML = `<span class="son-price-roll__strip">${cells.map((d) => `<span>${d}</span>`).join('')}</span>`;
    digit.style.setProperty('--son-roll-from', up ? 0 : steps);
    digit.style.setProperty('--son-roll-to', up ? steps : 0);
    digit.style.setProperty('--son-roll-delay', `${delay}ms`);
    digit.style.setProperty('--son-roll-width-from', `${widths[old]}px`);
    digit.style.setProperty('--son-roll-width-to', `${widths[next]}px`);

    return digit;
}

// Digits matched from the right, so the cents line up and a longer price grows on the left.
// A wheel turns once more when a wheel left of it changes, like a counter - one turn at most,
// or the cents of a big jump would spin hundreds of times. Without lap only changed digits move.
function buildRoll(text, from, lap, widths) {
    const roll = document.createElement('span');
    roll.className = 'son-price-roll';
    roll.dataset.shown = from;

    const label = document.createElement('span');
    label.className = 'screen-reader-text';
    label.textContent = text;

    const digits = document.createElement('span');
    digits.setAttribute('aria-hidden', 'true');

    const newDigits = text.replace(/\D/g, '');
    const oldDigits = from.replace(/\D/g, '').padStart(newDigits.length, '0');
    const up = Number(newDigits) > Number(oldDigits);
    const shift = oldDigits.length - newDigits.length;

    let index = 0;
    let rolled = 0;
    [...text].forEach((char) => {
        if (!/\d/.test(char)) {
            digits.append(char);
            return;
        }
        const old = oldDigits[index + shift];
        const higherChanged = lap && oldDigits.slice(0, index + shift) !== '0'.repeat(shift) + newDigits.slice(0, index);
        index += 1;

        if (old === char && !higherChanged) {
            digits.append(char);
            return;
        }
        digits.append(wheel(Number(old), Number(char), up, higherChanged, rolled * STAGGER, widths));
        rolled += 1;
    });

    roll.append(label, digits);

    return { roll, rolled };
}

function start(roll, text, rolled) {
    // a layout read commits the old positions before the transition starts
    void roll.offsetWidth;
    roll.classList.add('is-rolling');
    roll.dataset.shown = text;

    setTimeout(() => {
        if (roll.isConnected) {
            roll.replaceWith(text);
        }
    }, DURATION + rolled * STAGGER + 100);
}

function whenLoaded(node, fn) {
    if (!node.parentElement?.classList.contains('son-price-loading')) {
        fn();
        return;
    }
    document.addEventListener('son-price-loading-end', fn, { once: true });
}

/**
 * Rolls the number in textNode from the number shown before. Announces the change, so
 * discount-toast.js can tell a recalculation that moved something from one that did not.
 */
export function rollNumber(textNode, from, waitOn = textNode.parentElement, lap = true) {
    const text = textNode.textContent;
    if (!from || from === text) {
        return;
    }
    document.dispatchEvent(new CustomEvent('son-price-changed'));
    if (reducedMotion()) {
        return;
    }

    const { roll, rolled } = buildRoll(text, from, lap, digitWidths(textNode.parentElement));
    textNode.replaceWith(roll);
    whenLoaded(waitOn, () => start(roll, text, rolled));
}

// what a number element shows, also halfway through a roll
export { shownNumber, numberNode };

// nodes painted before: the first paint on a page load never rolls
const painted = new WeakSet();

// what the customer sees: the personal price, or the public one around a hidden node
function visibleAmounts(node) {
    if (!node.hidden) {
        return amounts(node);
    }

    return node.parentElement ? amounts(node.parentElement).filter((bdi) => !node.contains(bdi)) : [];
}

// Puts the new price HTML into the node, or hides it for the public price (html undefined).
// Whatever shows afterwards rolls from what showed before, public and personal alike.
export function paintPrice(node, html) {
    const first = !painted.has(node);
    painted.add(node);

    const before = {};
    if (!first) {
        visibleAmounts(node).forEach((bdi) => {
            before[role(bdi)] = shownNumber(bdi);
        });
    }

    if (html !== undefined) {
        node.innerHTML = html;
    }
    node.hidden = html === undefined;
    if (first || reducedMotion()) {
        return;
    }

    visibleAmounts(node).forEach((bdi) => {
        const textNode = numberNode(bdi);
        if (textNode) {
            rollNumber(textNode, before[role(bdi)], node);
        }
    });
}
